import {Component, Property} from "@wonderlandengine/api";

function isTouchDevice() {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

function normalizeXZ(v) {
  v[1] = 0;
  const length = Math.hypot(v[0], v[2]);
  if (length > 0.0001) {
    v[0] /= length;
    v[2] /= length;
  }
  return v;
}

export class MobileTouchControls extends Component {
  static TypeName = "mobile-touch-controls";
  static Properties = {
    headObject: Property.object(),
    speed: Property.float(2.0),
    deadZone: Property.float(0.12),
    lockY: Property.bool(true),
  };

  init() {
    this.active = false;
    this.pointerId = null;
    this.axis = [0, 0];
    this.startPosition = [0, 0, 0];
    this.forward = new Float32Array(3);
    this.right = new Float32Array(3);
    this.position = new Float32Array(3);
  }

  start() {
    if (!isTouchDevice()) return;
    this.active = true;
    this.startPosition = Array.from(this.object.getPositionWorld());
    this._createJoystick();
  }

  _createJoystick() {
    if (document.getElementById("mobile-joystick")) return;

    this.base = document.createElement("div");
    this.base.id = "mobile-joystick";
    this.base.style.cssText = [
      "position:fixed",
      "left:24px",
      "bottom:24px",
      "width:128px",
      "height:128px",
      "border-radius:50%",
      "background:rgba(0,0,0,0.28)",
      "border:2px solid rgba(255,255,255,0.35)",
      "z-index:2000",
      "touch-action:none",
      "display:flex",
      "align-items:center",
      "justify-content:center",
    ].join(";");

    this.knob = document.createElement("div");
    this.knob.style.cssText = [
      "width:54px",
      "height:54px",
      "border-radius:50%",
      "background:rgba(255,255,255,0.72)",
      "box-shadow:0 2px 10px rgba(0,0,0,0.35)",
      "transform:translate(0px,0px)",
    ].join(";");

    this.base.appendChild(this.knob);
    document.body.appendChild(this.base);

    this.base.addEventListener("pointerdown", this._onPointerDown.bind(this));
    window.addEventListener("pointermove", this._onPointerMove.bind(this));
    window.addEventListener("pointerup", this._onPointerUp.bind(this));
    window.addEventListener("pointercancel", this._onPointerUp.bind(this));
  }

  _onPointerDown(event) {
    if (this.pointerId !== null) return;
    this.pointerId = event.pointerId;
    this.base.setPointerCapture?.(event.pointerId);
    this._updateAxis(event);
  }

  _onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    this._updateAxis(event);
  }

  _onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.axis = [0, 0];
    if (this.knob) this.knob.style.transform = "translate(0px,0px)";
  }

  _updateAxis(event) {
    const rect = this.base.getBoundingClientRect();
    const radius = rect.width / 2;
    const centerX = rect.left + radius;
    const centerY = rect.top + radius;
    const maxDistance = radius - 18;
    let dx = event.clientX - centerX;
    let dy = event.clientY - centerY;
    const distance = Math.hypot(dx, dy);

    if (distance > maxDistance) {
      dx = (dx / distance) * maxDistance;
      dy = (dy / distance) * maxDistance;
    }

    this.axis = [dx / maxDistance, -dy / maxDistance];
    if (Math.hypot(this.axis[0], this.axis[1]) < this.deadZone) {
      this.axis = [0, 0];
    }

    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
  }

  update(dt) {
    if (!this.active || (this.axis[0] === 0 && this.axis[1] === 0)) return;

    const directionObject = this.headObject || this.object;
    directionObject.getForward(this.forward);
    directionObject.getRight(this.right);
    normalizeXZ(this.forward);
    normalizeXZ(this.right);

    this.object.getPositionWorld(this.position);
    const distance = this.speed * dt;
    this.position[0] +=
      (this.forward[0] * this.axis[1] + this.right[0] * this.axis[0]) *
      distance;
    this.position[2] +=
      (this.forward[2] * this.axis[1] + this.right[2] * this.axis[0]) *
      distance;
    if (this.lockY) this.position[1] = this.startPosition[1];
    this.object.setPositionWorld(this.position);
  }
}
